/**
 * Apply a field edit to BreakdownState.
 * Returns a new BreakdownState with the field updated and readiness recalculated.
 */

import type { ExtractionFieldData } from '@/components/intake/ExtractionField';
import type { BreakdownState } from './buildBreakdownState';
import { computeBreakdownReadiness } from './computeBreakdownReadiness';

export function applyFieldEdit(
  state: BreakdownState,
  fieldName: string,
  value: string | number,
): BreakdownState {
  const normalizedValue = typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : value;

  const existingField = state.fields[fieldName];
  const updatedField = {
    ...(existingField || {
      confidence: 0,
      source_snippet: null,
      isCritical: false,
      isRequiredToProceed: false,
    }),
    fieldName,
    value: normalizedValue,
    source: 'user' as const,
    confidence: 1.0,
    detection_status: 'detected' as const,
    confirmation_status: 'edited' as const,
    isEditing: false,
    editDraft: null,
    hasValidationError: false,
    validationError: null,
    touched: true,
    updated_at: new Date().toISOString(),
  };

  const fields = { ...state.fields, [fieldName]: updatedField };

  // Remove from missing fields
  const missingFields = state.missingFields.filter(f => f !== fieldName);

  // Build extraction-compatible map for readiness
  const extractionFields: Record<string, ExtractionFieldData> = {};
  for (const [k, v] of Object.entries(fields)) {
    extractionFields[k] = v;
  }

  const readiness = computeBreakdownReadiness(extractionFields, missingFields, state.conflicts);

  return {
    ...state,
    fields,
    missingFields,
    readiness,
  };
}

/**
 * Apply field acceptance (user accepts inferred value without editing)
 */
export function applyFieldAccept(
  state: BreakdownState,
  fieldName: string,
): BreakdownState {
  const field = state.fields[fieldName];
  if (!field || field.detection_status !== 'detected') return state;

  const updatedField = {
    ...field,
    confirmation_status: 'accepted' as const,
    touched: true,
    updated_at: new Date().toISOString(),
  };

  const fields = { ...state.fields, [fieldName]: updatedField };

  const extractionFields: Record<string, ExtractionFieldData> = {};
  for (const [k, v] of Object.entries(fields)) {
    extractionFields[k] = v;
  }

  const readiness = computeBreakdownReadiness(extractionFields, state.missingFields, state.conflicts);

  return { ...state, fields, readiness };
}

/**
 * Confirm dilemma — sets optionA, optionB, initialPreference + scores to confirmed
 */
export function applyDilemmaConfirm(state: BreakdownState): BreakdownState {
  const fields = { ...state.fields };

  for (const key of ['optionA', 'optionB', 'initialPreference']) {
    if (fields[key]) {
      fields[key] = { ...fields[key], confirmation_status: 'confirmed', touched: true, updated_at: new Date().toISOString() };
    }
  }

  // NOTE: Do NOT auto-confirm impactScore, urgencyScore, decisionType here.
  // These are never-autofill fields that require explicit user interaction.
  // Only the dilemma options (optionA, optionB, initialPreference) are confirmed by this action.

  const extractionFields: Record<string, ExtractionFieldData> = {};
  for (const [k, v] of Object.entries(fields)) {
    extractionFields[k] = v;
  }

  const readiness = computeBreakdownReadiness(extractionFields, state.missingFields, state.conflicts);

  return { ...state, fields, readiness, dilemmaConfirmed: true };
}
